import type { ReactElement } from "react";
import { Alert, Button, Card, Chip, Field, TextInput } from "../../ui";
import { ScopeTree } from "./ScopeTree";
import { ScopeTreeSkeleton } from "./Skeletons";
import { SHAPE_HINT, useSandboxForm } from "./SandboxForm.hook";
import { COUNTRIES, EXPIRY_PERIODS } from "./types";
import styles from "./SandboxForm.module.css";

export function SandboxForm(): ReactElement {
  const form = useSandboxForm();

  return (
    <div className={styles.page}>
      <header>
        <h1 className={styles.title}>New sandbox</h1>
        <p className={styles.intro}>
          You&rsquo;ll get a base URL, an API key and secret, and a credit
          balance. Scopes are set now and cannot be changed afterwards.
        </p>
      </header>

      {form.atQuota ? (
        <Alert tone="warning" title="You are at the sandbox limit">
          {form.active} of {form.maxActive} sandboxes are active. Delete one from
          the list to free up a slot, then come back.
        </Alert>
      ) : null}

      <Card>
        <div className={styles.fields}>
          <Field
            label="Name"
            hint={form.nameError ? undefined : SHAPE_HINT}
            error={form.nameError}
            required
          >
            <TextInput
              value={form.name}
              autoComplete="off"
              invalid={Boolean(form.nameError)}
              onChange={(event) => form.onNameChange(event.target.value)}
            />
          </Field>

          <div className={styles.group}>
            <span className={styles.label}>Country</span>
            <div className={styles.chips}>
              {COUNTRIES.map((option) => (
                <Chip
                  key={option}
                  tone="brand"
                  selected={form.country === option}
                  onClick={() => form.setCountry(option)}
                >
                  {option.toUpperCase()}
                </Chip>
              ))}
            </div>
          </div>

          <div className={styles.group}>
            <span className={styles.label}>Expiry period</span>
            <div className={styles.chips}>
              {EXPIRY_PERIODS.map((option) => (
                <Chip
                  key={option}
                  tone="brand"
                  selected={form.expiry === option}
                  onClick={() => form.setExpiry(option)}
                >
                  {option} days
                </Chip>
              ))}
            </div>
            <p className={styles.hint}>
              The sandbox and its data are deleted when it expires.
            </p>
          </div>
        </div>
      </Card>

      <Card>
        <div className={styles.scopesHead}>
          <div>
            <h2 className={styles.sectionTitle}>API key scopes</h2>
            <p className={styles.hint}>
              One level per group. Fixed groups cannot be changed.
            </p>
          </div>
          <Button
            size="sm"
            variant="tertiary"
            disabled={!form.scopeGroups}
            onClick={form.onResetScopes}
          >
            Reset
          </Button>
        </div>
        <div className={styles.scopes}>
          {form.scopeStatus === "error" ? (
            <Alert
              tone="danger"
              title="Could not load the scope catalogue"
              action={
                <Button size="sm" variant="secondary" onClick={form.onRetryScopes}>
                  Retry
                </Button>
              }
            >
              A sandbox keeps the scopes it is created with, so creating one
              needs the catalogue. Retry to carry on.
            </Alert>
          ) : null}
          {form.scopeGroups && form.selection ? (
            <>
              <ScopeTree
                mode="edit"
                groups={form.scopeGroups}
                selection={form.selection}
                onChange={form.setLevel}
              />
              {form.granted === 0 ? (
                <p className={styles.error}>
                  Grant at least one group so the API key can do something.
                </p>
              ) : null}
            </>
          ) : form.scopeStatus !== "error" ? (
            <ScopeTreeSkeleton />
          ) : null}
        </div>
      </Card>

      {form.serverError ? <Alert tone="danger">{form.serverError}</Alert> : null}

      <div className={styles.footer}>
        <p className={styles.summary}>
          Granting {form.granted} of {form.groupCount} groups · {form.fullAccess}{" "}
          at full access
        </p>
        <div className={styles.actions}>
          <Button
            disabled={!form.valid}
            loading={form.busy}
            onClick={form.onSubmit}
          >
            Create sandbox
          </Button>
          <Button variant="secondary" onClick={form.onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
